import React, { useMemo } from 'react';
import { Ledger } from '../src/types';
import Tooltip from './Tooltip';

interface LedgerViewProps {
  ledger: Ledger;
  onRollback?: () => void;
  canRollback?: boolean;
  activeCanonId?: string;
  activeProjectId?: string;
  onRepair?: () => void;
}

const LedgerView: React.FC<LedgerViewProps> = ({ ledger, onRollback, canRollback, activeCanonId, activeProjectId, onRepair }) => {
  const safeLedger = useMemo(() => {
    if (!activeCanonId || !activeProjectId) return ledger;
    
    const isAllowed = (item: any) => {
      if (!item || typeof item !== 'object') return true;
      if (item.canonId) return item.canonId === activeCanonId;
      if (item.projectId) return item.projectId === activeProjectId;
      return true;
    };

    return {
      ...ledger,
      inventory: (ledger.inventory || []).filter(isAllowed),
      plot_points: (ledger.plot_points || []).filter(isAllowed),
      resolved_plot_points: (ledger.resolved_plot_points || []).filter(isAllowed),
      characters: Object.fromEntries(Object.entries(ledger.characters || {}).filter(([_, v]) => isAllowed(v))),
      locations: Object.fromEntries(Object.entries(ledger.locations || {}).filter(([_, v]) => isAllowed(v))),
    };
  }, [ledger, activeCanonId, activeProjectId]);

  const getContent = (item: any) => typeof item === 'object' && item.content ? item.content : item;

  return (
    <div className="bg-cv-panel border border-cv-border-strong rounded-xl p-4 h-full flex flex-col shadow-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs uppercase tracking-[0.2em] text-cv-gold font-black flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Canon Memory
          <Tooltip text="Story Memory tracks character states, world rules, and past events to maintain continuity as your book grows." />
        </h3>
        <div className="flex items-center gap-2">
          {onRepair && (
            <button 
              onClick={onRepair}
              className="text-[9px] font-black text-cv-muted hover:text-cv-primary uppercase tracking-widest flex items-center gap-1.5 bg-cv-elevated/50 px-2 py-1 rounded border border-cv-border-strong/50 transition-all"
              title="Remove records belonging to other projects"
            >
              Repair Memory
            </button>
          )}
          {canRollback && onRollback && (
            <button 
              onClick={onRollback}
              className="text-[9px] font-black text-cv-warning hover:text-orange-300 uppercase tracking-widest flex items-center gap-1.5 bg-orange-950/30 px-2 py-1 rounded border border-orange-500/30 transition-all"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
              Rollback
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto space-y-4 pr-2 custom-scrollbar">
        {/* Characters */}
        <section>
          <h4 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Dramatis Personae</h4>
          <div className="space-y-2">
            {Object.entries(safeLedger.characters || {}).map(([name, data]: [string, any]) => (
              <div key={name} className="bg-cv-page/50 border border-cv-border rounded-lg p-2 hover:border-cv-interactive/30 transition-colors">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-cv-interactive-hover font-bold text-xs">{name}</span>
                  {data.status && (
                    <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border ${
                      data.status.toLowerCase().includes('dead') || data.status.toLowerCase().includes('missing') 
                        ? 'bg-cv-danger/15 border-cv-danger/50 text-cv-danger'
                        : 'bg-green-900/30 border-green-500/50 text-green-400'
                    }`}>
                      {data.status}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-cv-muted leading-relaxed italic">{data.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Locations */}
        <section>
          <h4 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Cartography</h4>
          <div className="grid grid-cols-1 gap-2">
            {Object.entries(safeLedger.locations || {}).map(([name, data]: [string, any]) => (
              <div key={name} className="bg-cv-page/50 border border-cv-border rounded-lg p-2">
                <span className="text-cv-success font-bold text-[11px] block mb-0.5">{name}</span>
                <p className="text-[9px] text-cv-muted leading-tight">{data.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Inventory */}
        <section>
          <h4 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Relics & Assets</h4>
          <div className="flex flex-wrap gap-1.5">
            {safeLedger.inventory?.map((item, i) => (
              <span key={i} className="bg-cv-panel text-cv-secondary text-[9px] px-2 py-0.5 rounded-full border border-cv-border font-mono">
                {getContent(item)}
              </span>
            ))}
          </div>
        </section>

        {/* Plot Points */}
        <section>
          <h4 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Canon Milestones</h4>
          <ul className="space-y-1.5">
            {safeLedger.plot_points?.map((point, i) => (
              <li key={i} className="flex gap-2 text-[10px] text-cv-muted leading-snug">
                <span className="text-cv-interactive font-black">•</span>
                {getContent(point)}
              </li>
            ))}
          </ul>
        </section>

        {/* Resolved Plot Points */}
        {safeLedger.resolved_plot_points && safeLedger.resolved_plot_points.length > 0 && (
          <section>
            <h4 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Resolved Milestones</h4>
            <ul className="space-y-1.5 opacity-50">
              {safeLedger.resolved_plot_points.map((point, i) => (
                <li key={i} className="flex gap-2 text-[10px] text-cv-muted leading-snug line-through">
                  <span className="text-cv-disabled font-black">✓</span>
                  {getContent(point)}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      
      <div className="mt-4 pt-3 border-t border-cv-border flex justify-between items-center text-[9px] font-mono text-cv-disabled uppercase tracking-widest">
        <span>Last Sync: {new Date(safeLedger.last_updated || ledger.last_updated).toLocaleTimeString()}</span>
        <span className="text-cv-interactive/50">v{safeLedger.arc_stage || ledger.arc_stage || '1.0'}</span>
      </div>
    </div>
  );
};

export default LedgerView;
